// tabToggle — Jul 2026 (Randy). "Turn off tabs": a hamburger toggle that hides
// every edge tab/bump on the Q&A + AI Assistant pages and replaces them with a
// dropdown selector inside the hamburger menu. Device-wide (localStorage).
//
// How it works: each edge-tab component (SideBumps, LengthBump, DiscoverBumps,
// DynamicBumps, SiloAssistant edge tabs) registers its tabs here on mount and
// hides its handles when the toggle is ON. The hamburger lists the registered
// tabs; picking one fires "mysilo:open-tab" and the owning component opens its
// drawer exactly as if the side handle had been tapped.
import { useEffect, useState } from "react";

const KEY = "mysilo_tabs_off";
const TOGGLE_EVT = "mysilo:tabs-off-changed";
const REG_EVT = "mysilo:tabs-registry-changed";
const OPEN_EVT = "mysilo:open-tab";

// Jul 25, 2026 (Randy) — PLATFORM DEFAULT: tabs hidden on MOBILE, visible on
// DESKTOP. Only applies when the user has never touched the toggle; an
// explicit "0"/"1" from a returning user is always respected.
export const getTabsOff = () => {
  const mobileDefault = () => {
    try { return window.matchMedia("(max-width: 767px)").matches; } catch { return true; }
  };
  try {
    const v = localStorage.getItem(KEY);
    if (v === null || v === undefined) return mobileDefault();
    return v === "1";
  } catch { return mobileDefault(); }
};

export const setTabsOff = (v) => {
  try { localStorage.setItem(KEY, v ? "1" : "0"); } catch { /* private mode */ }
  window.dispatchEvent(new Event(TOGGLE_EVT));
};

export function useTabsOff() {
  const [off, setOff] = useState(getTabsOff());
  useEffect(() => {
    const h = () => setOff(getTabsOff());
    window.addEventListener(TOGGLE_EVT, h);
    return () => window.removeEventListener(TOGGLE_EVT, h);
  }, []);
  return off;
}

// ---- registry of the current page's hideable tabs ----
const registry = new Map(); // id -> [{key, label}]

export function registerHiddenTabs(id, tabs) {
  registry.set(id, tabs);
  window.dispatchEvent(new Event(REG_EVT));
  return () => { registry.delete(id); window.dispatchEvent(new Event(REG_EVT)); };
}

export function useHiddenTabList() {
  const [list, setList] = useState([]);
  useEffect(() => {
    const h = () => setList(Array.from(registry.values()).flat());
    h();
    window.addEventListener(REG_EVT, h);
    return () => window.removeEventListener(REG_EVT, h);
  }, []);
  return list;
}

export const openHiddenTab = (key) =>
  window.dispatchEvent(new CustomEvent(OPEN_EVT, { detail: { key } }));

export function useOpenTabListener(handler) {
  useEffect(() => {
    const h = (e) => handler(e.detail?.key);
    window.addEventListener(OPEN_EVT, h);
    return () => window.removeEventListener(OPEN_EVT, h);
  });
}
